import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Plus } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatGBP, formatDateUK } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";

export const KIND_LABEL = { supplier_payment: "Supplier payment", customer_collection: "Customer payment received", cash_deposit: "Cash into bank",
  cash_withdrawal: "Cash taken out", other_cash_receipt: "Other cash in" };
const STATUS_CLS = { draft: "text-navy/50", submitted: "text-emerald-600", approved: "text-emerald-600", rejected: "text-red-600", voided: "text-navy/40 line-through", cancelled: "text-navy/40 line-through", reversed: "text-red-600 line-through" };
export const STATUS_LABEL = { draft: "Draft (not counted)", submitted: "Recorded", approved: "Recorded", rejected: "Returned", voided: "Voided", cancelled: "Cancelled", reversed: "Reversed" };

export default function PaymentsPage() {
  const navigate = useNavigate();
  const { activeStoreId } = useAuth();
  const [rows, setRows] = useState(null);
  const [balances, setBalances] = useState(null);
  useEffect(() => {
    const q = activeStoreId ? `?store_id=${activeStoreId}` : "";
    api.get(`/payments${q}`).then((r) => setRows(r.data)).catch(() => {});
    api.get(`/supplier-balances${q}`).then((r) => setBalances(r.data.filter((b) => b.outstanding !== 0))).catch(() => {});
  }, [activeStoreId]);
  return (
    <div className="space-y-5" data-testid="payments-page">
      <button type="button" onClick={() => navigate("/dashboard")} className="flex items-center gap-1 text-sm text-navy/60 min-h-[44px]" data-testid="payments-back-btn"><ArrowLeft className="w-4 h-4" /> Back</button>
      <div className="flex items-center justify-between"><h1 className="text-2xl font-bold text-navy">Payments & cash</h1>
        <button type="button" onClick={() => navigate("/record-payment")} className="min-h-[44px] px-4 rounded-full bg-gold text-navy font-semibold flex items-center gap-1" data-testid="new-payment-btn"><Plus className="w-4 h-4" />New</button></div>
      <Card className="border-navy/5 shadow-none"><CardContent className="p-4">
        <h2 className="font-semibold text-navy mb-2">Owed to suppliers</h2>
        {balances === null && <p className="text-sm text-navy/50">Loading…</p>}
        {balances && !balances.length && <p className="text-sm text-navy/50">Nothing outstanding.</p>}
        <ul data-testid="supplier-balances">{(balances || []).map((b) => (
          <li key={b.supplier_id} className="flex justify-between py-2 border-b border-navy/5 last:border-0 text-sm" data-testid={`supplier-balance-${b.supplier_id}`}>
            <span className="text-navy">{b.supplier}</span><span className="font-medium text-navy">{formatGBP(b.outstanding)}</span></li>))}</ul>
      </CardContent></Card>
      <ul className="space-y-2" data-testid="payments-list">
        {(rows || []).map((p) => (
          <li key={p.id} onClick={() => navigate(`/payments/${p.id}`)} className="bg-white rounded-xl border border-navy/5 p-4 cursor-pointer" data-testid={`payment-row-${p.id}`}>
            <div className="flex justify-between text-sm"><span className="font-medium text-navy">{KIND_LABEL[p.kind]}</span>
              <span className={STATUS_CLS[p.status]} data-testid={`payment-status-${p.id}`}>{STATUS_LABEL[p.status]}</span></div>
            <div className="flex justify-between text-xs text-navy/50 mt-1"><span>{formatDateUK(p.movement_date)} · {p.method.replace("_", " ")}{p.reference ? ` · ${p.reference}` : ""}</span>
              <span className="text-navy font-semibold text-sm">{formatGBP(p.amount)}</span></div>
          </li>
        ))}
        {rows === null && <p className="text-sm text-navy/50">Loading…</p>}
        {rows && !rows.length && <p className="text-sm text-navy/50">No payments recorded yet.</p>}
      </ul>
    </div>
  );
}
